// BackgroundController.js - Manages continuous background across sections
// Add this to your main App.js or create as a separate component

import { useEffect } from 'react';

export const useBackgroundController = () => {
    useEffect(() => {
        // Global particle system that spans all sections
        const createGlobalParticles = () => {
            const canvas = document.createElement('canvas');
            canvas.id = 'global-particles';
            canvas.style.position = 'fixed';
            canvas.style.top = '0';
            canvas.style.left = '0';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.pointerEvents = 'none';
            canvas.style.zIndex = '0';
            document.body.appendChild(canvas);

            const ctx = canvas.getContext('2d');
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;

            const particles = [];
            const particleCount = 120;

            class GlobalParticle {
                constructor() {
                    this.x = Math.random() * canvas.width;
                    this.y = Math.random() * canvas.height;
                    this.size = Math.random() * 3 + 0.5;
                    this.speedX = (Math.random() - 0.5) * 0.5;
                    this.speedY = (Math.random() - 0.5) * 0.5;
                    this.opacity = Math.random() * 0.5 + 0.1;
                    this.color = Math.random() > 0.5 ? '201, 169, 110' : '248, 248, 248';
                }

                update() {
                    this.x += this.speedX;
                    this.y += this.speedY;

                    // Continuous flow - wrap around screen
                    if (this.x > canvas.width) this.x = 0;
                    if (this.x < 0) this.x = canvas.width;
                    if (this.y > canvas.height) this.y = 0;
                    if (this.y < 0) this.y = canvas.height;

                    // React to scroll
                    const scrollFactor = window.scrollY * 0.001;
                    this.y += scrollFactor;
                }

                draw() {
                    ctx.beginPath();
                    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
                    ctx.fill();
                }
            }

            // Initialize particles
            for (let i = 0; i < particleCount; i++) {
                particles.push(new GlobalParticle());
            }

            // Animation loop
            const animate = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                particles.forEach(particle => {
                    particle.update();
                    particle.draw();
                });
                requestAnimationFrame(animate);
            };

            animate();

            // Handle resize
            const handleResize = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                canvas.remove();
            };
        };

        // Parallax scrolling effect for sections
        const handleScroll = () => {
            const scrolled = window.scrollY;
            // Updated to match your component structure
            const sections = document.querySelectorAll('#Home, #About, #Services, #Projects, #Portfolio, #Contact, .home-container, .About-out-container, .services-container');

            sections.forEach((section, index) => {
                const speed = 0.5 + (index * 0.1);
                const yPos = -(scrolled * speed);

                // Apply parallax to background elements
                const bgElements = section.querySelectorAll('.gradient-orb, .floating-shapes, .floating-shapes-about, .shape');
                bgElements.forEach(element => {
                    element.style.transform = `translateY(${yPos * 0.5}px)`;
                });
            });

            // Update CSS variable for dynamic effects
            document.documentElement.style.setProperty('--scroll-y', scrolled + 'px');
        };

        // Initialize
        const cleanup = createGlobalParticles();
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (cleanup) cleanup();
        };
    }, []);
};

// Usage in App.js:
// import { useBackgroundController } from './BackgroundController';
//
// function App() {
//   useBackgroundController();
//
//   return (
//     <div className="App">
//       <NavBar />
//       <Home />
//       <About />
//       {/* Other sections */}
//     </div>
//   );
// }